import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { StatusBadge, StatusBadgeSm } from '../../utils/StatusBadge';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import DocumentMetadataPanel from './DocumentMetadataPanel';
import DeleteConfirmationModal from '../../utils/DeleteConfirmationModal';
import RetriggerConfirmationModal from '../../utils/RetriggerConfirmationModal';
import TrimContent from '../../utils/TrimContent';
import PaginationControls from '../../utils/PaginationControls';
import { useNavigate } from "react-router-dom";
import { useDispatch } from 'react-redux';
import authNavigate from '../../globalComponents/AuthNav';
import { openRefLibraryForDoc } from '../../../store/unstructuredThreadSlice';

// The consumer extracts references only on these types; the flag is ignored on the rest.
const REF_EXTRACT_TYPES = ['pdf', 'word', 'text'];

const CheckBoxForDocSelect = ({doc_id, checked, onChange }) => {
  
  const handleCheckboxChange = (event) => {
    onChange(doc_id, event.target.checked);
  }
  
  return (
    <input type="checkbox" className="form-check-input mb-1" checked={checked} onChange={handleCheckboxChange} />
  );
};

const DocumentStatusTable = ({ threadId, onDocumentsUpdated, externalRefreshTrigger = 0 ,onThreadStatusRefresh, onFolderDeleted, threadTags: initialThreadTags = [], onPanelOpenChange}) => {
  const dispatch = useDispatch();
  // State variables for document status table
  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortField, setSortField] = useState('document_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectAll, setSelectAll] = useState(false);
  
  // Delete document states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  // Select document states
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [canRetrigger, setCanRetrigger] = useState(false);
  const [canDeleteDocument, setCanDeleteDocument] = useState(false);
  const [canEditMetadata, setCanEditMetadata] = useState(false);
  const [showDeleteSelectedConfirm, setShowDeleteSelectedConfirm] = useState(false);
  
  const [docPercentages,setDocPrecentages] = useState({
    upl_sta:0,stand_sta:0,md_rag_sta:0,img_rag_sta:0,auto_class_sta:0,parquet_sta:0
  })
  
  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');

  // Document preview states
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState(null);
  const [previewDocumentName, setPreviewDocumentName] = useState(null);
   const [metadataList, setMetadataList] = useState([]);
  const [metadataTotal, setMetadataTotal] = useState(0);
  const [showDetailed, setShowDetailed] = useState(false);

  const getOverallStatus = (doc) => {
    const statuses = [
      doc.upload_status,
      doc.standardization_status,
      doc.markdown_rag_status,
      doc.image_rag_status,
      doc.autoclassification_status,
      doc.parquet_status,
    ].filter(s => s !== 'DISABLED' && s !== 'INAPPLICABLE');

    if (statuses.includes('FAILED')) return 'FAILED';
    if (statuses.includes('RUNNING')) return 'RUNNING';
    if (statuses.includes('WAITING')) return 'WAITING';
    if (statuses.some(s => s == null || s === 'NOT BEGUN')) return 'NOT BEGUN';
    return statuses.length > 0 ? 'COMPLETED' : 'NOT BEGUN';
  };

  // References are extracted during standardization, so the pill opens the
  // library only once that stage has completed.
  const renderRefPill = (doc) => {
    if (!doc.ref_extract || !REF_EXTRACT_TYPES.includes((doc.document_type || '').toLowerCase())) {
      return null;
    }
    const ready = doc.standardization_status === 'COMPLETED';
    const tooltip = ready
      ? 'View references extracted from this document'
      : doc.standardization_status === 'FAILED'
        ? 'Standardization failed, so no references were extracted'
        : 'References will be available once standardization completes';
    return (
      <button
        className="ev-btn-blue-outline-round ev-btn-xsmall flex-shrink-0 avatar-tooltip"
        data-tooltip={tooltip}
        disabled={!ready}
        onClick={() => dispatch(openRefLibraryForDoc({ docId: doc.id, docName: doc.document_name }))}
      >
        <i className="bi bi-book"></i> Ref
      </button>
    );
  };

  const [showRetriggerConfirm, setShowRetriggerConfirm] = useState(false);
  const [retriggerProcessType, setRetriggerProcessType] = useState('');
  const [retriggerProcessName, setRetriggerProcessName] = useState('');
  const [threadTags, setThreadTags] = useState(initialThreadTags);

  // API base URL
  const API_BASE_URL = '/pceapi/threads/unstructured';

  // HTTP headers with authorization
  const headers = {
    Authorization: GetCookie('access_token')
  };

  // Initialize searchInput with search value
  useEffect(() => {
    setSearchInput(search);
  }, []);

  // Watch for external refresh trigger
  useEffect(() => {
    if (externalRefreshTrigger > 0) {
      setRefreshTrigger(prev => prev + 1);
    }
  }, [externalRefreshTrigger]);

  // Sync threadTags when parent passes updated tags
  useEffect(() => {
    if (initialThreadTags.length > 0) {
      setThreadTags(initialThreadTags);
    }
  }, [initialThreadTags]);


  // Fetch documents on component mount and when dependencies change
  useEffect(() => {
    fetchDocuments();
  }, [threadId, page, size, search, sortField, sortDirection, refreshTrigger]);

  // Replace your existing fetchDocuments function with this
const fetchDocuments = async () => {
  if (!threadId) return;
  
  setIsProcessingPopupVisible(true);
  setProcessingMessage('Loading documents');
  
  try {
    // Fetch both document status and metadata in parallel
    const [statusResponse, metadataResponse] = await Promise.all([
      axios.post(
        `${API_BASE_URL}/document-status`, 
        {
          thread_id: threadId,
          page,
          size,
          search: search || undefined,
          sort_field: sortField,
          sort_direction: sortDirection
        },
        { headers }
      ),
      axios.post(
        `${API_BASE_URL}/document-metadata`,
        {
          thread_id: threadId,
          page,
          size,
          search: search || undefined,
          sort_field: sortField,
          sort_direction: sortDirection,
        },
        { headers }
      )
    ]);

    // Handle status response
    const statusData = statusResponse.data;
    if (statusData.status === 'NotAuthorized') {
      setIsProcessingPopupVisible(false);
      authNavigate('/login', 'iam', navigate);
      return;
    }
    
    if (statusData.status === 'failed') {
      setIsProcessingPopupVisible(false);
      // Folder deleted by another user: clear its rows (they'd otherwise stay
      // on screen, e.g. stuck at "Uploading") and let the page warn and close.
      if (statusData.code === 'FOLDER_DELETED') {
        setDocuments([]);
        setTotal(0);
        if (onFolderDeleted) {
          onFolderDeleted(statusData.message);
          return;
        }
      }
      setPopupErrorMessage(statusData.message || 'Failed to load documents');
      setIsErrorPopupVisible(true);
      return;
    }
    
    setDocuments(statusData.data || []);
    setTotal(statusData.total || 0);
    setDocPrecentages(statusData.percent);
    // Thread-level rights, answered by the same checks the retrigger and
    // delete-document endpoints run.
    setCanRetrigger(Boolean(statusData.can_retrigger));
    setCanDeleteDocument(Boolean(statusData.can_delete_document));
    setCanEditMetadata(Boolean(statusData.can_edit_metadata));

    // Handle metadata response
    const metadataData = metadataResponse.data;
    if (metadataData.status === 'success' || metadataData.status !== 'failed') {
      setMetadataList(metadataData.data || []);
      setMetadataTotal(metadataData.total || 0);
    }
    
    // If callback provided, notify parent that documents have been updated
    if (onDocumentsUpdated) {
      onDocumentsUpdated(statusData.data || []);
    }
  } catch (error) {
    setPopupErrorMessage(error.message || 'Failed to load documents');
    setIsErrorPopupVisible(true);
  } finally {
    setIsProcessingPopupVisible(false);
  }
};

  // Handle page change
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Handle size change
  const handleSizeChange = (event) => {
    setSize(parseInt(event.target.value, 25));
    setPage(1); // Reset to first page when changing size
  };

  // Handle search input change
  const handleSearchInputChange = (event) => {
    setSearchInput(event.target.value);
  };

  // Handle search button click
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1); // Reset to first page when search changes
  };
  
  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Handle search form submission
  const handleSearchSubmit = (event) => {
    event.preventDefault();
    handleSearch();
  };

  // Handle sort change
  const frontendSortFields = ['overall_status'];

  const statusOrder = { 'RUNNING': 1, 'WAITING': 2, 'NOT BEGUN': 3, 'FAILED': 4, 'COMPLETED': 5 };

  const sortedDocuments = sortField === 'overall_status'
    ? [...documents].sort((a, b) => {
        const aStatus = statusOrder[getOverallStatus(a)] ?? 5;
        const bStatus = statusOrder[getOverallStatus(b)] ?? 5;
        return sortDirection === 'asc' ? aStatus - bStatus : bStatus - aStatus;
      })
    : documents;

  const handleSortChange = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    if (!frontendSortFields.includes(field)) {
      setPage(1);
    }
  };

  // Render sort arrow
  const renderSortArrow = (field) => {
    if (sortField !== field) return null;
    
    return (
      <span className="ms-1">
        {sortDirection === 'asc' ? 
          <i className="bi bi-caret-up-fill"></i> : 
          <i className="bi bi-caret-down-fill"></i>
        }
      </span>
    );
  };

  // Handle refresh button click
  const handleRefresh = () => {
    if (onThreadStatusRefresh) {
      onThreadStatusRefresh();
      return;
    }

    setRefreshTrigger(prev => prev + 1);
  };

  // Close error popup
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };
  
  // Close success popup
  const closeSuccessPopup = () => {
    setIsSuccessPopupVisible(false);
  };
  
  // Show delete confirmation dialog
  const confirmDelete = (document) => {
    setDocumentToDelete(document);
    setShowDeleteConfirm(true);
  };
  
  const handleDocSelect = (document_id, to_add) => {
  let newSelectedDocuments;
  
  if (to_add === false) {
    newSelectedDocuments = selectedDocuments.filter(id => id !== document_id);
  } else {
    newSelectedDocuments = [...selectedDocuments, document_id];
  }
  
  setSelectedDocuments(newSelectedDocuments);
  
  // Update selectAll state based on whether all documents are selected
  const allDocIds = documents.map(doc => doc.id);
  const allSelected = allDocIds.every(id => newSelectedDocuments.includes(id)) && allDocIds.length > 0;
  setSelectAll(allSelected);
};

// Add this useEffect to reset selectAll when documents change (pagination, search, etc.)
useEffect(() => {
  const allDocIds = documents.map(doc => doc.id);
  const allSelected = allDocIds.every(id => selectedDocuments.includes(id)) && allDocIds.length > 0;
  setSelectAll(allSelected);
}, [documents, selectedDocuments]);

  // Handle delete selected documents
  const handleDeleteSelectedDocuments = async() => {
    setShowDeleteSelectedConfirm(false);
    if (selectedDocuments.length > 0) {
      setIsProcessingPopupVisible(true);
      setProcessingMessage(`Deleting ${selectedDocuments.length} selected documents`);

      try {
        const res = await axios.delete(
          `${API_BASE_URL}/delete-many-documents`,{headers,
          data : {
            doc_ids: selectedDocuments,
            thread_id: threadId
          }}
        );
      const data = res.data;
      
      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (data.status === 'success') {
        setPopupSuccessMessage(data.message || 'Documents deleted successfully');
        setIsSuccessPopupVisible(true)
      }
      if (data.status === 'failed') {
        setPopupErrorMessage(data.message || 'Failed to delete documents');
        setIsErrorPopupVisible(true);
      }
      
    }
    catch (error) {
      setPopupErrorMessage(error.message || 'Failed to load documents');
      setIsErrorPopupVisible(true);
    } finally {
      setSelectedDocuments([]);
      setIsProcessingPopupVisible(false);
      handleRefresh();
    }
  };
}

  // Handle single document deletion
  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    
    if (!documentToDelete) return;
    
    setIsProcessingPopupVisible(true);
    setProcessingMessage(`Deleting document ${documentToDelete.document_name}`);
    
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/delete-document`,
        { 
          headers,
          data: {
            document_id: documentToDelete.id,
            thread_id: threadId
          }
        }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to delete document');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setPopupSuccessMessage(data.message || 'Document deleted successfully');
      setIsSuccessPopupVisible(true);
      
      // Refresh document and thread-level statuses together.
      handleRefresh();
    } catch (error) {
      console.error('Error deleting document:', error);
      setPopupErrorMessage(error.message || 'Failed to delete document');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
      setDocumentToDelete(null);
    }
  };
  
  // Generate body content for delete modal
  const getDeleteModalBodyContent = () => {
    if (!documentToDelete) return null;
    
    return (
      <div>
        <p className="text-danger mb-0">This action cannot be undone.</p>
      </div>
    );
  };

  // Generate body content for delete selected modal
  const getDeleteSelectedModalBodyContent = () => {
    return (
      <div>
        <p className="mb-2">
          You are about to delete <strong>{selectedDocuments.length}</strong> selected documents.
        </p>
        <p className="text-danger mb-0">This action cannot be undone.</p>
      </div>
    );
  };

  // Pagination controls
  const totalPages = Math.ceil(total / size);
  const paginationItems = [];
  
  // Create pagination links
  const maxPagesToShow = 5;
  let startPage = Math.max(1, page - Math.floor(maxPagesToShow / 2));
  let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
  
  if (endPage - startPage + 1 < maxPagesToShow) {
    startPage = Math.max(1, endPage - maxPagesToShow + 1);
  }
  
  // Previous button
  paginationItems.push(
    <li key="prev" className={`page-item ${page === 1 ? 'disabled' : ''}`}>
      <button 
        className="ev-btn-light-fill me-1" 
        
        onClick={() => handlePageChange(page - 1)}
        disabled={page === 1}
      >
        <i className="bi bi-chevron-left"></i>
      </button>
    </li>
  );
  
  // Page numbers
  for (let i = startPage; i <= endPage; i++) {
    paginationItems.push(
      <li key={i} className={`page-item ${page === i ? 'active' : ''}`}>
        <button 
           className={`ev-btn-light-fill me-1 ${page === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      </li>
    );
  }
  
  // Next button
  paginationItems.push(
    <li key="next" className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
      <button 
         className="ev-btn-light-fill me-1" 
        onClick={() => handlePageChange(page + 1)}
        disabled={page === totalPages}
      >
        <i className="bi bi-chevron-right"></i>
      </button>
    </li>
  );

  const handlePreviewDocument = (doc_id, documentName) => {
    setPreviewDocId(doc_id);
    setPreviewDocumentName(documentName);
    setPreviewOpen(true);
  };

  // Lets the Data Folder page hide its own header while the panel is open.
  useEffect(() => {
    if (onPanelOpenChange) onPanelOpenChange(previewOpen);
  }, [previewOpen]);

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewDocId(null);
    setPreviewDocumentName(null);
  };
 const handleRetrigger = (processType) => {
  const processNames = {
    migration: "Migration",
    standardization: "Standardization",
    markdownrag: "Markdown RAG",
    imagerag: "Image RAG",
    autoclassifier: "AutoClassifier",
    parquet:"Parquet"
  };

  const processName = processNames[processType] || processType;
  
  setRetriggerProcessType(processType);
  setRetriggerProcessName(processName);
  setShowRetriggerConfirm(true);
};

const navigate = useNavigate();

const handleConfirmRetrigger = async () => {
  setShowRetriggerConfirm(false);
  
  if (!threadId) return;
  
  setIsProcessingPopupVisible(true);
  setProcessingMessage(`Triggering ${retriggerProcessName} process...`);

  try {
    const response = await axios.post(
      `${API_BASE_URL}/retrigger`,
      {
        thread_id: threadId,
        process_type: retriggerProcessType,
      },
      { headers }
    );

    const data = response.data;

    if (data.status === "NotAuthorized") {
      setIsProcessingPopupVisible(false);
      authNavigate('/login', 'iam', navigate);
      return;
    }

    if (data.status === "failed") {
      setIsProcessingPopupVisible(false);
      setPopupErrorMessage(data.message || `Failed to trigger ${retriggerProcessName} process`);
      setIsErrorPopupVisible(true);
      return;
    }

    setPopupSuccessMessage(`${retriggerProcessName} process triggered successfully`);
    setIsSuccessPopupVisible(true);

    // Refresh document and thread-level statuses after the trigger is accepted.
    setTimeout(() => {
      handleRefresh();
    }, 2000);

  } catch (error) {
    console.error(`Error triggering ${retriggerProcessName} process:`, error);
    setPopupErrorMessage(`Failed to trigger ${retriggerProcessName} process`);
    setIsErrorPopupVisible(true);
  } finally {
    setIsProcessingPopupVisible(false);
  }
};

  const handleSelectAllToggle = () => {
  const pageIds = documents.map(doc => doc.id);
  const pageFullySelected = pageIds.length > 0 && pageIds.every(id => selectedDocuments.includes(id));

  if (pageFullySelected) {
    setSelectedDocuments(prev => prev.filter(id => !pageIds.includes(id)));
  } else {
    setSelectedDocuments(prev => [...new Set([...prev, ...pageIds])]);
  }
};

  return (
    <div className={`document-status-table ${previewOpen ? '' : 'mt-5'}`}>
      {/* The list is replaced by the document panel while it is open */}
      {!previewOpen && (
      <>
          {/* Document Status View */}
          <div className="row mt-1 mb-3">
            <div className="col-6 d-flex align-items-center position-relative">
              <p className="ev-reg-p-blue mb-0 w-20">Documents ({total})</p>

              {/* Search form */}
              <div className="w-60"> 
              <input
                type="text"
                className="form-control search-input ms-0"
                placeholder="Search document name"
                value={searchInput}
                onChange={handleSearchInputChange}
                onKeyPress={handleSearchKeyPress}
              />
              <button 
                className="ev-btn-lightblue-fill-round search-bar-button" 
                onClick={handleSearch}
              >
                <i className="bi bi-search"></i>
              </button>
              </div>
            </div>
            
            <div className="col-6 text-end">
              {/* View switch only — no rights attached, so it stays for everyone. */}
              <button
                className="ev-btn-blue-fill-round ev-bold-reg ms-2"
                onClick={() => setShowDetailed(prev => !prev)}
              >
                <i className={`bi ${showDetailed ? 'bi-grid' : 'bi-list-columns'} me-1`}></i>
                {showDetailed ? 'Simple View' : 'Document Insight'}
              </button>
              {canDeleteDocument && (
                <button 
                  className="ev-btn-red-fill-round ms-2" 
                  disabled={selectedDocuments.length === 0}
                  onClick={() => setShowDeleteSelectedConfirm(true)}
                >
                  Delete selected ({selectedDocuments.length})
                </button>
              )}
              <button 
                className="ev-btn-lightblue-fill-round ev-bold-reg ms-2"  
                onClick={handleRefresh}
              >
                <i className="bi bi-arrow-clockwise"></i> Refresh Documents
              </button>
            </div>
          </div>
          
          {documents.length === 0 && !isProcessingPopupVisible ? (
            <div className="alert alert-primary">No documents found in this Data Folder.</div>
          ) : !showDetailed ? (
            <div className="table-container">
              <table className="table-custom1 mt-0 overflow-hidden w-100">
                <thead>
                  <tr>
                    {canDeleteDocument && (
                      <th>
                        <input
                          type="checkbox"
                          className="form-check-input mb-1"
                          checked={documents.length > 0 && documents.every(doc => selectedDocuments.includes(doc.id))}
                          onChange={handleSelectAllToggle}
                        />
                      </th>
                    )}
                    <th onClick={() => handleSortChange('document_name')} style={{ cursor: 'pointer' }}>
                      <label style={{ cursor: 'pointer' }}>Document Name {renderSortArrow('document_name')}</label>
                    </th>
                    <th onClick={() => handleSortChange('document_size_mb')} style={{ cursor: 'pointer' }}>
                      <label style={{ cursor: 'pointer' }}>Size (MB) {renderSortArrow('document_size_mb')}</label>
                    </th>
                    <th onClick={() => handleSortChange('overall_status')} style={{ cursor: 'pointer' }}>
                      <span style={{ cursor: 'pointer' }}>Document Status {renderSortArrow('overall_status')}</span>
                    </th>
                    {canDeleteDocument && (
                      <th className="text-center"><label>Actions</label></th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {sortedDocuments.map((doc, index) => (
                    <tr key={index}>
                      {canDeleteDocument && (
                        <td className="align-top">
                          <CheckBoxForDocSelect
                            doc_id={doc.id}
                            checked={selectedDocuments.includes(doc.id)}
                            onChange={handleDocSelect}
                          />
                        </td>
                      )}
                      <td className="align-top">
                        <div className="d-flex align-items-center flex-nowrap gap-2">
                          {/* Name tooltip lives on its own wrapper (not the <td>), so it no longer
                              stacks with the View & Edit tooltip. The wrapper is not truncated, so
                              overflow:hidden on the inner span cannot clip the tooltip. */}
                          <div className="ev-min-w-0 avatar-tooltip" data-tooltip={doc.document_name || 'Uploading...'}>
                            <span className="text-truncate d-block">
                              <TrimContent text={doc.document_name || 'Uploading...'} maxLength={50} />
                            </span>
                          </div>
                          <button
                            onClick={() => handlePreviewDocument(doc.id, doc.document_name || 'Unnamed Document')}
                            className="ev-btn-lightblue-fill-round ev-btn-xsmall flex-shrink-0 avatar-tooltip"
                            data-tooltip="View document and metadata"
                          >
                            <i className="bi bi-eye"></i> View & Edit
                          </button>
                          {renderRefPill(doc)}
                        </div>
                      </td>
                      <td className="align-top">{doc.document_size_mb.toFixed(2)}</td>
                      <td className="align-top">
                        <StatusBadgeSm status={getOverallStatus(doc)} />
                      </td>
                      {canDeleteDocument && (
                        <td className="align-top text-center">
                          <button
                            className="btn ev-btn-red-fill-round ev-btn-med ev-radius-xsmall pe-2 ps-2 avatar-tooltip"
                            onClick={() => confirmDelete(doc)}
                            data-tooltip="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="table-custom1 mt-0 overflow-hidden w-100 table-fixed">
                  <colgroup>
                    {canDeleteDocument && <col className="col-status-check" />}
                    <col className="col-status-name" />
                    <col className="col-status-size" />
                    <col className="col-status-upload" />
                    <col className="col-status-stand" />
                    <col className="col-status-mdrag" />
                    <col className="col-status-imgrag" />
                    <col className="col-status-auto" />
                    <col className="col-status-parquet" />
                    {canDeleteDocument && <col className="col-status-actions" />}
                  </colgroup>
                  <thead>
                    {/* Whole row is retrigger controls; with none of them
                        rendered it was just a band of empty header cells. */}
                    {canRetrigger && (
                      <tr>
                        {canDeleteDocument && <th></th>}
                        <th></th>
                        <th></th>
                        <th></th>
                        <th className="p-0">
                          {canRetrigger && (
                           <button
                           className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                           onClick={() => handleRetrigger('standardization')}
                         >
                           <i className="bi bi-arrow-clockwise"></i> Retrigger
                         </button>
                         )}
                       </th>
                       <th  className="p-0">
                         {canRetrigger && (
                           <button
                           className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                           onClick={() => handleRetrigger('markdownrag')}
                         >
                           <i className="bi bi-arrow-clockwise"></i> Retrigger
                         </button>
                         )}
                       </th>
                       <th className="p-0">
                         {canRetrigger && (
                           <button
                           className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                           onClick={() => handleRetrigger('imagerag')}
                         >
                           <i className="bi bi-arrow-clockwise"></i> Retrigger
                         </button>
                         )}
                       </th>
                       <th className="p-0">
                         {canRetrigger && (
                           <button
                           className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                           onClick={() => handleRetrigger('autoclassifier')}
                         >
                           <i className="bi bi-arrow-clockwise"></i> Retrigger
                         </button>
                         )}
                       </th>
                       <th className="p-0">
                         {canRetrigger && (
                           <button
                           className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                           onClick={() => handleRetrigger('parquet')}
                         >
                           <i className="bi bi-arrow-clockwise"></i> Retrigger
                         </button>
                         )}
                       </th>
                       {canDeleteDocument && <th></th>}
                     </tr>
                    )}
                   <tr className="cursor-pointer">
                     {canDeleteDocument && (
                       <th>
                         <input 
                           type="checkbox"
                           className="form-check-input mb-1"
                           checked={selectAll}
                           onChange={handleSelectAllToggle}
                           disabled={documents.length === 0}
                         />
                       </th>
                     )}
                     <th onClick={() => handleSortChange('document_name')}>
                       <label>Document Name {renderSortArrow('document_name')}</label>
                     </th>
                     <th onClick={() => handleSortChange('document_size_mb')}>
                       <label>Size (MB) {renderSortArrow('document_size_mb')}</label>
                     </th>
                     <th onClick={() => handleSortChange('upload_status')}>
                       <label>Upload {renderSortArrow('upload_status')}<br/>{docPercentages.upl_sta}%</label>
                     </th>
                     <th onClick={() => handleSortChange('standardization_status')}>
                       <label>Standardization {renderSortArrow('standardization_status')}<br/>{docPercentages.stand_sta}%</label>
                     </th>
                     <th onClick={() => handleSortChange('markdown_rag_status')}>
                       <label>Markdown RAG {renderSortArrow('markdown_rag_status')}<br/>{docPercentages.md_rag_sta}%</label>
                     </th>
                     <th onClick={() => handleSortChange('image_rag_status')}>
                       <label>Image RAG {renderSortArrow('image_rag_status')}<br/>{docPercentages.img_rag_sta}%</label>
                     </th>
                     <th onClick={() => handleSortChange('autoclassification_status')}>
                       <label>AutoClassifer {renderSortArrow('autoclassification_status')}<br/>{docPercentages.auto_class_sta}%</label>
                     </th>
                     <th onClick={() => handleSortChange('parquet_status')}>
                       <label>Parquet {renderSortArrow('parquet_status')}<br/>{docPercentages.parquet_sta}%</label>
                     </th>
                     {canDeleteDocument && (
                       <th>
                         <label>Actions</label>
                       </th>
                     )}
                   </tr>
                  </thead>
                </table>

                <div className="scrollable-element vh-75">
                  <table className="table-custom1 overflow-hidden w-100 table-fixed">
                    <colgroup>
                      {canDeleteDocument && <col className="col-status-check" />}
                      <col className="col-status-name" />
                      <col className="col-status-size" />
                      <col className="col-status-upload" />
                      <col className="col-status-stand" />
                      <col className="col-status-mdrag" />
                      <col className="col-status-imgrag" />
                      <col className="col-status-auto" />
                      <col className="col-status-parquet" />
                      {canDeleteDocument && <col className="col-status-actions" />}
                    </colgroup>
                    <tbody>
                      {documents.map((doc, index) => (
                        <tr key={index}>
                          {canDeleteDocument && (
                            <td className="align-top">
                              <CheckBoxForDocSelect doc_id={doc.id} checked={selectedDocuments.includes(doc.id)} onChange={handleDocSelect} />
                            </td>
                          )}
                          <td className="align-top">
                            <div className="d-flex align-items-center flex-nowrap gap-2">
                              {/* Name tooltip lives on its own wrapper (not the <td>), so it no longer
                                  stacks with the View & Edit tooltip. The wrapper is not truncated, so
                                  overflow:hidden on the inner span cannot clip the tooltip. */}
                              <div className="ev-min-w-0 avatar-tooltip" data-tooltip={doc.document_name || 'Uploading...'}>
                                <span className="text-truncate d-block">
                                  <TrimContent text={doc.document_name || 'Uploading...'} maxLength={20} />
                                </span>
                              </div>
                              <button 
                                onClick={() => handlePreviewDocument(doc.id, doc.document_name || 'Unnamed Document')} 
                                className="ev-btn-lightblue-fill-round ev-btn-xsmall flex-shrink-0 avatar-tooltip"
                            data-tooltip="View document and metadata"
                          >
                            <i className="bi bi-eye"></i> View & Edit
                          </button> 
                              {renderRefPill(doc)}
                            </div>
                          </td>
                          <td className="align-top">{doc.document_size_mb.toFixed(2)}</td>
                          <td className="align-top"><StatusBadge status={doc.upload_status} fullWidth={true} /></td>
                          <td className="align-top"><StatusBadge status={doc.standardization_status} fullWidth={true} /></td>
                          <td className="align-top"><StatusBadge status={doc.markdown_rag_status} fullWidth={true} /></td>
                          <td className="align-top"><StatusBadge status={doc.image_rag_status} fullWidth={true} /></td>
                          <td className="align-top"><StatusBadge status={doc.autoclassification_status} fullWidth={true} /></td>
                          <td className="align-top"><StatusBadge status={doc.parquet_status} fullWidth={true} /></td>
                          {canDeleteDocument && (
                            <td className="align-top text-center">
                              <button
                                className="btn ev-btn-red-fill-round ev-btn-med ev-radius-xsmall pe-2 ps-2 avatar-tooltip" 
                                onClick={() => confirmDelete(doc)}
                                data-tooltip="Delete"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Pagination — sits outside the Simple View / Document Insight
              ternary so both table layouts get the same footer controls. */}
          {total > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              {/* Items per page selector */}
              <div>
                <PaginationControls
                  initialSize={size}
                  onSizeChange={(newSize) => {
                    setSize(newSize);
                    setPage(1);
                  }}
                />
              </div>
              <div className="ev-small-p-dark">
                Showing {Math.min((page - 1) * size + 1, total)} to {Math.min(page * size, total)} of {total} documents
              </div>
              {totalPages > 1 ? (
                <nav aria-label="Document pagination">
                  <ul className="pagination pagination-sm mb-0 me-w">
                    {paginationItems}
                  </ul>
                </nav>
              ) : <div />}
            </div>
          )}

        </>
      )}

        {/* Document panel: preview on the left, metadata on the right.
            Previous / Next follow the list the user opened it from. */}
        <DocumentMetadataPanel
          isOpen={previewOpen}
          onClose={closePreview}
          docId={previewDocId}
          navItems={documents.map(d => ({ id: d.id, name: d.document_name || 'Unnamed Document' }))}
          metadataList={metadataList}
          threadId={threadId}
          threadTags={threadTags}
          canEditMetadata={canEditMetadata}
          onSaved={handleRefresh}
        />

        {/* Popups */}
        {isProcessingPopupVisible && (
          <ProcessingPopup message={processingMessage} />
        )}
        
        {isErrorPopupVisible && (
          <ErrorPopup 
            onClose={closeErrorPopup}
            message={popupErrorMessage}
          />
        )}
        
        {isSuccessPopupVisible && (
          <SuccessPopup 
            onClose={closeSuccessPopup}
            message={popupSuccessMessage}
          />
        )}
        
        {/* Single Document Delete Confirmation Modal */}
        <DeleteConfirmationModal
          show={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setDocumentToDelete(null);
          }}
          onConfirm={handleDelete}
          objectName="document"
          itemName={documentToDelete?.document_name}
          bodyContent={getDeleteModalBodyContent()}
        />

        {/* Multiple Documents Delete Confirmation Modal */}
        <DeleteConfirmationModal
          show={showDeleteSelectedConfirm}
          onClose={() => {
            setShowDeleteSelectedConfirm(false);
            setSelectedDocuments([]);
          }}
          onConfirm={handleDeleteSelectedDocuments}
          objectName="documents"
          itemName={`${selectedDocuments.length} selected documents`}
          bodyContent={getDeleteSelectedModalBodyContent()}
        />

        {/* Retrigger Confirmation Modal */}
        <RetriggerConfirmationModal
          show={showRetriggerConfirm}
          onClose={() => setShowRetriggerConfirm(false)}
          onConfirm={handleConfirmRetrigger}
          processName={retriggerProcessName}
          bodyContent={
            <p className="text-danger">
              This will restart failed or stuck documents for this stage in this Data Folder.
            </p>
          }
        />
      </div>
    );
  };

  export default DocumentStatusTable;